import styled from 'styled-components';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PatternFormat } from 'react-number-format';

import { config } from '_config';
import { theme } from '_constants/theme';
import { FIELDS_KEYS } from '_constants/forms';
import { ICONS_ALIASES } from '_constants/icons';
import { getHostname } from '_helpers/common';

import { sendMessage } from '_redux/contactUs/slice';
import {
  selectResult,
  selectError,
  selectLoading,
} from '_redux/contactUs/selector';

import ContactUsForm from '_components/composed/forms/ContactUsForm';
import Icon from '_components/atomic/Icon';
import Card from '_components/atomic/layouts/Card';

const StyledOuterContainer = styled.div`
  padding: ${theme.spaces.smallX}px;
  position: relative;

  @media ${theme.devices.tablet} {
    padding: unset;
  }
`;

const StyledInnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${theme.spaces.regular}px;
  border-radius: 16px;
  border: 1px solid ${theme.colors.gray[200]};
  background-color: ${theme.colors.gray[25]};

  @media ${theme.devices.tablet} {
    border: none;
    background-color: unset;
  }
`;

const StyledContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: ${theme.spaces.large23X * 10 + 10}px;
  width: 70%;
  padding-top: ${theme.spaces.large6X}px;
  margin-bottom: ${theme.spaces.large6X}px;

  @media ${theme.devices.tablet} {
    width: 100%;
    padding-top: ${theme.spaces.regular}px;
  }
`;

const StyledLink = styled.a`
  display: inline-block;
  margin-left: ${theme.spaces.regular}px;
  margin-right: ${theme.spaces.regular}px;
  padding-top: ${theme.spaces.smallX}px;
  padding-bottom: ${theme.spaces.smallX}px;
  white-space: nowrap;
  color: ${theme.colors.indigo[600]};

  span {
    color: ${theme.colors.indigo[600]};
  }
`;

const ContactUsPage = () => {
  const dispatch = useDispatch();
  const result = useSelector(selectResult);
  const error = useSelector(selectError);
  const loading = useSelector(selectLoading);

  const handleSubmit = useCallback(
    (values) => {
      if (values) {
        dispatch(
          sendMessage({
            category: 'INC',
            domain: getHostname(),
            name: values[FIELDS_KEYS.fullName],
            email: values[FIELDS_KEYS.email],
            subject: values[FIELDS_KEYS.subject],
            message: values[FIELDS_KEYS.message],
          })
        );
      }
    },
    [dispatch]
  );

  return (
    <StyledOuterContainer>
      <StyledInnerContainer>
        <StyledContentBox>
          <Card.Text
            size={theme.fontSizes.large6X}
            weight={600}
            color={theme.colors.indigo[600]}
            centered
            desktop
            bottomSpace={theme.spaces.large}
          >
            Contact Us
          </Card.Text>
          <Card.Text
            size={theme.fontSizes.large2X}
            weight={600}
            color={theme.colors.indigo[600]}
            centered
            mobile
            bottomSpace={theme.spaces.regular}
          >
            Contact Us
          </Card.Text>
          <Card.Text
            size={theme.fontSizes.regular}
            weight={400}
            lineHeightRatio={1.5}
            centered
            desktop
            bottomSpace={theme.spaces.regular * 2}
          >
            If you have any questions or need assistance with our services,
            please do not hesitate to reach out to us.
            <br />
            Our team is available to answer your questions and provide support
            Monday through Friday from 9am to 5pm.
            <br />
            You can contact us via phone, email, or by filling out the form on
            this page.
            <br />
            <br />
            <StyledLink href={'tel:' + config.phone}>
              <Icon
                alias={ICONS_ALIASES.phone}
                color={theme.colors.indigo[600]}
              />
              <PatternFormat
                value={config.phone?.substring(2)}
                displayType="text"
                format="(###) ###-####"
              />
            </StyledLink>
            <StyledLink href={`mailto:${config.email}`}>
              {config.email}
            </StyledLink>
          </Card.Text>
          <Card.Text
            size={theme.fontSizes.small}
            weight={400}
            lineHeightRatio={1.5}
            centered
            mobile
            bottomSpace={theme.spaces.regular}
          >
            If you have any questions or need assistance with our services,
            please do not hesitate to reach out to us.
            <br />
            Our team is available to answer your questions and provide support
            Monday through Friday from 9am to 5pm.
            <br />
            You can contact us via phone, email, or by filling out the form on
            this page.
            <br />
            <br />
            <StyledLink href={'tel:' + config.phone}>
              <Icon
                alias={ICONS_ALIASES.phone}
                color={theme.colors.indigo[600]}
              />
              <PatternFormat
                value={config.phone?.substring(2)}
                displayType="text"
                format="(###) ###-####"
              />
            </StyledLink>
            <StyledLink href={`mailto:${config.email}`}>
              {config.email}
            </StyledLink>
          </Card.Text>
          <Card.Text
            size={theme.fontSizes.regular}
            weight={400}
            lineHeightRatio={1.5}
            centered
            desktop
            bottomSpace={theme.spaces.regular * 2}
          >
            If you prefer to contact us by filling out the form on this page,
            please provide your name, email address, and a brief message
            describing how we can help you. We will do our best to respond to
            your inquiry as soon as possible.
          </Card.Text>
          <Card.Text
            size={theme.fontSizes.small}
            weight={400}
            lineHeightRatio={1.5}
            centered
            mobile
            bottomSpace={theme.spaces.regular}
          >
            If you prefer to contact us by filling out the form on this page,
            please provide your name, email address, and a brief message
            describing how we can help you. We will do our best to respond to
            your inquiry as soon as possible.
          </Card.Text>

          <ContactUsForm
            error={error}
            success={result}
            submitDisabled={loading}
            onSubmit={handleSubmit}
          />
        </StyledContentBox>
      </StyledInnerContainer>
    </StyledOuterContainer>
  );
};

export default ContactUsPage;
